import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { LoadingContent } from 'Shared/PageElements';
import React, { Suspense } from 'react';
import { LastLocationProvider } from 'react-router-last-location';
import { ToastMessageWrapper } from '../Features/ToastMessages';
import { GlobalMessageWrapper } from '../GlobalMessages';
import { FeatureFlags } from '../State';
import ApplicationErrorBoundary from './ApplicationErrorBoundary';
import ConversationHub from './ConversationHub';
import { AuthorizationInterceptor } from './Interceptors/AuthorizationInterceptor';
import RootApp from './RootApp';
import ServiceProviders from './ServiceProviders';
import useSubscription from './Subscription/useSubscription';
import UploadHandlers from './UploadHandlers';
import './i18n';
import { getToken } from '@clerk/react';
import { AuthProvider } from './Types/AuthProviders';

const clerkJwtTemplate = { template: 'jwt-aud' };
const clerkTokenRefreshIntervalMs = 30 * 1000;

type MainProps = {
    token: string;
};

const Auth0MainWithAuth = withAuthenticationRequired(Auth0Main);

function MainWrapper(): React.ReactElement {
    const { authProvider } = useSubscription();

    return authProvider === AuthProvider.Clerk ? <ClerkMain /> : <Auth0MainWithAuth />;
}

function Main({ token }: MainProps): React.ReactElement {
    const { authProvider } = useSubscription();
    return (
        <ApplicationErrorBoundary>
            <ServiceProviders token={token}>
                <FeatureFlags.Loaders.FeatureFlagsLoader />
                <ConversationHub token={token} />
                <ToastMessageWrapper />
                <GlobalMessageWrapper />
                {authProvider === AuthProvider.Auth0 && <AuthorizationInterceptor />}
                <UploadHandlers />
                <LastLocationProvider>
                    <Suspense fallback={<LoadingContent />}>
                        <RootApp />
                    </Suspense>
                </LastLocationProvider>
            </ServiceProviders>
        </ApplicationErrorBoundary>
    );
}

function ClerkMain(): React.ReactElement {
    const [token, setToken] = React.useState<string>('');

    React.useEffect(() => {
        let isMounted = true;

        const refreshToken = () => {
            getToken(clerkJwtTemplate).then((nextToken) => {
                if (!isMounted || !nextToken) return;

                setToken((currentToken) => (currentToken === nextToken ? currentToken : nextToken));
            });
        };

        refreshToken();
        const intervalId = window.setInterval(refreshToken, clerkTokenRefreshIntervalMs);

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, []);

    if (!token) return <LoadingContent />;

    return <Main token={token} />;
}

function Auth0Main(): React.ReactElement {
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const { subscription } = useSubscription();

    const [token, setToken] = React.useState('');
    const [tokenPending, setTokenPending] = React.useState(false);
    const [tokenLoaded, setTokenLoaded] = React.useState(false);

    React.useEffect(() => {
        if (tokenPending || tokenLoaded) return;

        setTokenPending(true);

        getAccessTokenSilently({
            audience: subscription.audience,
        })
            .then((tokenResponse) => {
                setToken(tokenResponse);
                setTokenLoaded(true);
                setTokenPending(false);
            })
            .catch((e) => {
                if (e.error === 'login_required') loginWithRedirect();
                if (e.error === 'consent_required') loginWithRedirect();
                throw e;
            });
    }, [tokenPending, tokenLoaded, getAccessTokenSilently, loginWithRedirect, subscription.audience]);

    if (!token) return <LoadingContent />;

    return <Main token={token} />;
}

export default MainWrapper;
